import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
    X,
    Save,
    CalendarClock,
    Paperclip,
    Upload,
    Trash2,
    CheckCircle2,
    Clock,
    XCircle,
    Download,
    Lock,
    MessageSquarePlus,
    MessageCircle,
    Send,
} from "lucide-react";

const formatDateTime = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
};

const SessionDialog = ({ session, onClose, onSaved, users, module = "pas" }) => {
    const [status, setStatus] = useState(session.status);
    const [conductedDate, setConductedDate] = useState(session.conducted_date || new Date().toISOString().slice(0,10));
    const [scheduledDate, setScheduledDate] = useState(session.scheduled_date);
    const [notes, setNotes] = useState(session.notes || "");
    const [attachments, setAttachments] = useState(session.attachments || []);
    const [comments, setComments] = useState(session.comments || []);
    const [submittedAt, setSubmittedAt] = useState(session.submitted_at || null);
    const [submittedBy, setSubmittedBy] = useState(session.submitted_by || null);
    const [newComment, setNewComment] = useState("");
    const [posting, setPosting] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    const employee = users.find((u) => u.user_id === session.employee_id);
    const manager = users.find((u) => u.user_id === session.manager_id);
    const submitter = users.find((u) => u.user_id === submittedBy);

    const isLocked = !!submittedAt;
    const isRoa = module === "roa";
    const basePath = isRoa ? "/roa/sessions" : "/sessions";
    const moduleLabel = isRoa ? "ROA Session" : "PAS Session";
    const accentText = isRoa ? "text-indigo-600" : "text-[#2EB4AC]";

    useEffect(() => {
        setStatus(session.status);
        setConductedDate(session.conducted_date || new Date().toISOString().slice(0,10));
        setScheduledDate(session.scheduled_date);
        setNotes(session.notes || "");
        setAttachments(session.attachments || []);
        setComments(session.comments || []);
        setSubmittedAt(session.submitted_at || null);
        setSubmittedBy(session.submitted_by || null);
        if (!session.attachments && (session.attachments_count ?? 0) > 0) {
            api.get(`${basePath}/${session.id}`).then((r) => {
                setAttachments(r.data.attachments || []);
                setComments(r.data.comments || []);
            }).catch(() => {});
        }
    }, [session]);

    const refetch = async () => {
        const r = await api.get(`${basePath}/${session.id}`);
        setAttachments(r.data.attachments || []);
        setComments(r.data.comments || []);
        setSubmittedAt(r.data.submitted_at || null);
        setSubmittedBy(r.data.submitted_by || null);
        setStatus(r.data.status);
        setNotes(r.data.notes || "");
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = { status, scheduled_date: scheduledDate, notes };
            if (status === "conducted") body.conducted_date = conductedDate;
            const r = await api.put(`${basePath}/${session.id}`, body);
            // Immediately reflect server-side state (submitted_at, status) in the dialog
            // so the UI locks without waiting for the parent reload.
            const fresh = r.data || {};
            if (fresh.submitted_at) {
                setSubmittedAt(fresh.submitted_at);
                setSubmittedBy(fresh.submitted_by || null);
            }
            if (fresh.status) setStatus(fresh.status);
            if (fresh.notes !== undefined) setNotes(fresh.notes || "");
            if (fresh.scheduled_date) setScheduledDate(fresh.scheduled_date);
            if (fresh.conducted_date) setConductedDate(fresh.conducted_date);
            toast.success(fresh.submitted_at ? "Session submitted and locked" : "Session updated");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete Cycle #${session.cycle_number} (${session.scheduled_date})? This cannot be undone.`)) return;
        try {
            await api.delete(`${basePath}/${session.id}`);
            toast.success("Session deleted");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await fetch(`${API}${basePath}/${session.id}/attachments`, {
                method: "POST",
                body: fd,
                credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("pas_session_token") || ""}` },
            });
            if (!r.ok) { const d = await r.json(); throw new Error(d?.detail || "Upload failed"); }
            await refetch();
            toast.success("File uploaded");
        } catch (err) {
            toast.error(err.message || "Upload failed");
        } finally {
            e.target.value = "";
        }
    };

    const removeAttachment = async (idx) => {
        try {
            await api.delete(`${basePath}/${session.id}/attachments/${idx}`);
            setAttachments(attachments.filter((_, i) => i !== idx));
            toast.success("Removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const download = (att) => {
        const a = document.createElement("a");
        a.href = `data:${att.mime};base64,${att.data}`;
        a.download = att.name;
        a.click();
    };

    const postComment = async () => {
        const text = newComment.trim();
        if (!text) return;
        setPosting(true);
        try {
            const r = await api.post(`${basePath}/${session.id}/comments`, { text });
            setComments((c) => [...c, r.data]);
            setNewComment("");
            toast.success("Update added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setPosting(false);
        }
    };

    const delComment = async (cid) => {
        if (!window.confirm("Delete this update?")) return;
        try {
            await api.delete(`${basePath}/${session.id}/comments/${cid}`);
            setComments((c) => c.filter((x) => x.id !== cid));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
            <div className="bg-white border border-slate-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" data-testid="session-dialog">
                <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200 bg-white rounded-t-lg flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <CalendarClock className={`h-4 w-4 ${accentText}`} />
                        <h3 className="font-heading font-semibold text-slate-900">{moduleLabel} · Cycle #{session.cycle_number}</h3>
                        {isLocked && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-purple-100 text-purple-800 border-purple-200">
                                <Lock className="h-3 w-3" /> Submitted
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" aria-label="Close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto scrollbar-thin flex-1">
                    {isLocked && (
                        <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-4 flex items-start gap-3">
                            <Lock className="h-4 w-4 text-[#25908A] mt-0.5" />
                            <div className="text-sm text-slate-700">
                                <div className="font-medium text-slate-900">This session is locked.</div>
                                Submitted {formatDateTime(submittedAt)}{submitter ? ` by ${submitter.name}` : ""}.
                                Original notes and attachments can't be changed. Add new information as updates below — each update is time-stamped and attributed.
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Employee</div>
                            <div className="font-medium text-slate-900">{employee?.name || "—"}</div>
                            <div className="text-slate-500">{employee?.email}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Manager</div>
                            <div className="font-medium text-slate-900">{manager?.name || "—"}</div>
                            <div className="text-slate-500">{manager?.email}</div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Status</label>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { v: "upcoming", label: "Upcoming", icon: Clock, cls: "bg-blue-50 text-blue-700 border-blue-200", active: "bg-blue-500 text-white border-blue-500" },
                                { v: "conducted", label: "Conducted", icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200", active: "bg-green-500 text-white border-green-500" },
                                { v: "missed", label: "Missed", icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200", active: "bg-red-500 text-white border-red-500" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => !isLocked && setStatus(o.v)}
                                    disabled={isLocked}
                                    data-testid={`status-${o.v}`}
                                    className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-medium transition-colors ${status===o.v ? o.active : o.cls} ${isLocked ? "opacity-70 cursor-not-allowed" : ""}`}
                                >
                                    <o.icon className="h-4 w-4" /> {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Scheduled date</label>
                            <input
                                type="date"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                disabled={isLocked}
                                className="h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                            />
                        </div>
                        {(status === "conducted" || conductedDate) && (
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Conducted date</label>
                                <input
                                    type="date"
                                    data-testid="conducted-date-input"
                                    value={conductedDate}
                                    onChange={(e) => setConductedDate(e.target.value)}
                                    disabled={isLocked}
                                    className="h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Original notes {isLocked && <span className="text-[10px] ml-1 text-slate-400 normal-case font-normal">(locked)</span>}</label>
                        <textarea
                            rows={4}
                            data-testid="session-notes-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Summary of discussion, action items, goals…"
                            disabled={isLocked}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none disabled:bg-slate-50 disabled:text-slate-600 resize-none"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                Attachments {isLocked && <span className="text-[10px] ml-1 text-slate-400 normal-case font-normal">(locked)</span>}
                            </label>
                            {!isLocked && (
                                <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors">
                                    <Upload className="h-4 w-4" /> Upload
                                </button>
                            )}
                            <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />
                        </div>
                        {attachments.length === 0 ? (
                            <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-6 text-sm text-slate-500 text-center">
                                {isLocked ? "No attachments were uploaded with this submission." : "No attachments. Upload notes, action items, or screenshots (max 5MB)."}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {attachments.map((a, i) => (
                                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Paperclip className="h-4 w-4 text-slate-500 flex-shrink-0" />
                                            <div className="truncate text-sm text-slate-900">{a.name}</div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => download(a)} className="h-8 w-8 rounded-md hover:bg-white text-slate-600 flex items-center justify-center" aria-label="Download"><Download className="h-4 w-4" /></button>
                                            {!isLocked && (
                                                <button onClick={() => removeAttachment(i)} className="h-8 w-8 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Updates / Comments Timeline — available only after first submission */}
                    {isLocked ? (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <MessageCircle className="h-4 w-4 text-[#25908A]" />
                                <h4 className="font-heading font-semibold text-slate-900">Updates</h4>
                                <span className="text-xs text-slate-500">{comments.length} update{comments.length!==1?"s":""}</span>
                            </div>

                            {comments.length > 0 && (
                                <div className="relative pl-6 mb-4">
                                    <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
                                    {comments.map((c) => (
                                        <div key={c.id} className="relative pb-4 last:pb-0" data-testid={`comment-${c.id}`}>
                                            <div className="absolute -left-[18px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#2EB4AC] ring-4 ring-white" />
                                            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-slate-900 truncate">{c.author_name || c.author_email}</div>
                                                        <div className="text-[11px] text-slate-500">{formatDateTime(c.created_at)}</div>
                                                    </div>
                                                    <button onClick={() => delComment(c.id)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Delete update">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                <div className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap">{c.text}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <textarea
                                    rows={3}
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a new update (time-stamped)…"
                                    data-testid="new-comment-input"
                                    className="w-full p-3 text-sm text-slate-900 outline-none resize-none border-0 focus:ring-0"
                                />
                                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50">
                                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                                        <MessageSquarePlus className="h-3 w-3" />
                                        Updates are append-only and time-stamped
                                    </div>
                                    <button
                                        onClick={postComment}
                                        disabled={posting || !newComment.trim()}
                                        data-testid="post-comment-btn"
                                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send className="h-3.5 w-3.5" />
                                        {posting ? "Posting…" : "Post update"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="border border-slate-200 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500 inline-flex items-center gap-2">
                            <MessageCircle className="h-3.5 w-3.5" />
                            Updates will be available after you submit this session for the first time.
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-2 bg-white rounded-b-lg flex-shrink-0">
                    <button
                        onClick={remove}
                        data-testid="delete-session-dialog-btn"
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors"
                    >
                        <Trash2 className="h-4 w-4" /> Delete
                    </button>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="h-11 px-5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 font-medium transition-colors">Close</button>
                        {!isLocked && (
                            <button onClick={save} disabled={saving} data-testid="save-session-btn" className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors disabled:opacity-50">
                                <Save className="h-4 w-4" /> {saving ? "Saving…" : (status === "conducted" || status === "missed" ? "Submit & lock" : "Save")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SessionDialog;
